import { DateTimeFormat } from '@billy/design-system'
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfMonth,
  isAfter,
  max,
  min,
  startOfMonth,
} from 'date-fns'

import { getCurrentLocale } from '../../../../../utils'
import { SalesReportGraphBucket } from '../enums/salesReportGraphBucket'
import { SalesReportFormattedGraphData } from '../types/salesReportFormattedGraphData'
import { getEmptyGraphValues } from './getEmptyGraphValues'

export interface SalesReportGraphTemplateBucket extends SalesReportFormattedGraphData {
  endDate: Date
  startDate: Date
}

interface GetSalesReportGraphTemplateResponse {
  bucket: SalesReportGraphBucket
  template: SalesReportGraphTemplateBucket[]
}

export const getSalesReportGraphBucket = (startDate: Date, endDate: Date): SalesReportGraphBucket => {
  const inclusiveDays = differenceInCalendarDays(endDate, startDate) + 1

  if (inclusiveDays <= 31) {
    return SalesReportGraphBucket.Day
  }

  if (!isAfter(endDate, addMonths(startDate, 3))) {
    return SalesReportGraphBucket.Week
  }

  return SalesReportGraphBucket.Month
}

export const getSalesReportGraphTemplate = (startDate: Date, endDate: Date): GetSalesReportGraphTemplateResponse => {
  const locale = getCurrentLocale()
  const bucket = getSalesReportGraphBucket(startDate, endDate)
  let ranges: Array<{ endDate: Date; startDate: Date }>

  if (bucket === SalesReportGraphBucket.Day) {
    ranges = eachDayOfInterval({ start: startDate, end: endDate }).map((date) => ({
      endDate: date,
      startDate: date,
    }))
  } else if (bucket === SalesReportGraphBucket.Week) {
    const numberOfWeeks = Math.ceil((differenceInCalendarDays(endDate, startDate) + 1) / 7)
    ranges = Array.from({ length: numberOfWeeks }, (_, index) => {
      const rangeStart = addDays(startDate, index * 7)

      return {
        endDate: min([addDays(rangeStart, 6), endDate]),
        startDate: rangeStart,
      }
    })
  } else {
    ranges = eachMonthOfInterval({ start: startDate, end: endDate }).map((date) => ({
      endDate: min([endOfMonth(date), endDate]),
      startDate: max([startOfMonth(date), startDate]),
    }))
  }

  const template = ranges.map(({ endDate: rangeEnd, startDate: rangeStart }, index) => {
    const title = DateTimeFormat.getFormattedDateTime(rangeStart, locale, {
      day: bucket === SalesReportGraphBucket.Month ? undefined : 'numeric',
      month: 'short',
    })
    const formattedStart = DateTimeFormat.getFormattedDateTime(rangeStart, locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    const formattedEnd = DateTimeFormat.getFormattedDateTime(rangeEnd, locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    const tooltip = (() => {
      if (bucket === SalesReportGraphBucket.Month) {
        return DateTimeFormat.getFormattedDateTime(rangeStart, locale, {
          month: 'long',
          year: 'numeric',
        })
      }

      return differenceInCalendarDays(rangeEnd, rangeStart) === 0
        ? formattedStart
        : `${formattedStart} – ${formattedEnd}`
    })()

    return {
      display: { title, tooltip },
      endDate: rangeEnd,
      id: `${rangeStart.getTime()}-${rangeEnd.getTime()}`,
      key: index,
      startDate: rangeStart,
      values: getEmptyGraphValues(),
    }
  })

  return { bucket, template }
}
